import React from 'react';
import { Star, Quote } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo, reviews } from '../mock';
import SEO from '../components/SEO';

const ReviewsPage = () => {
  return (
    <div className="min-h-screen bg-cream-50">
      <SEO 
        title="Customer Reviews - 5 Star Dog Grooming Manchester"
        description="Read 100+ five-star reviews from our happy customers. See why Pups & Dreams is Manchester's top-rated dog grooming salon in Denton. Real reviews from real pet parents."
        keywords="dog grooming reviews Manchester, pet grooming reviews Manchester, best dog groomers Manchester, 5 star dog grooming Manchester, dog grooming reviews Denton, customer testimonials Manchester dog grooming"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-sage-50 to-sage-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-sage-900">
              Customer Reviews
            </h1>
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) =>
                <Star key={i} className="fill-amber-400 text-amber-400" size={32} />
                )}
              </div>
              <span className="font-bold text-4xl ml-3 text-sage-900">{businessInfo.rating.stars}</span>
              <span className="text-xl text-sage-700">/ 5.0</span>
            </div>
            <p className="text-xl text-sage-700">
              Based on {businessInfo.rating.count}+ reviews from our wonderful customers
            </p>
          </div>
        </div>
      </section>

      {/* Review Stats */}
      <section className="!pt-[20px] !pb-[20px] bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
            { label: 'Total Reviews', value: businessInfo.rating.count + '+' },
            { label: 'Average Rating', value: businessInfo.rating.stars },
            { label: '5-Star Reviews', value: '95%' },
            { label: 'Happy Customers', value: '100%' }].
            map((stat, index) =>
            <div key={index} className="text-center p-6 bg-gradient-to-br from-sage-50 to-sage-100 rounded-xl">
                <div className="text-3xl font-bold text-sage-900 mb-1">{stat.value}</div>
                <div className="text-sm text-sage-600">{stat.label}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* All Reviews */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review) =>
            <Card key={review.id} className="border-sage-200 bg-white hover:shadow-xl transition-all hover:-translate-y-1 relative overflow-hidden">
                <div className="absolute top-4 right-4 text-sage-200">
                  <Quote size={48} className="opacity-50" />
                </div>
                <CardContent className="p-6 relative z-10">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) =>
                  <Star key={i} className="fill-amber-400 text-amber-400" size={18} />
                  )}
                  </div>
                  <p className="text-sage-700 mb-4 leading-relaxed italic">
                    "{review.text}"
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-sage-100">
                    <div>
                      <div className="font-bold text-sage-900">{review.author}</div>
                      <div className="text-sm text-sage-600">{review.date}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Google Reviews CTA */}
      <section className="bg-gradient-to-br from-sage-50 to-white !pt-[40px] !pb-[80px]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-sage-500 to-sage-600 flex items-center justify-center mx-auto mb-6 rounded-full">
              <Star className="text-white fill-white" size={40} />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-4">
              See More on Google
            </h2>
            <p className="text-lg text-sage-700 mb-8">
              Read our full collection of reviews on Google and see why pet owners in Denton 
              and Manchester trust us with their beloved dogs
            </p>
            <a
              href="https://www.google.com/search?q=pups+and+dreams+denton"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gradient-to-r from-sage-500 to-sage-600 hover:from-sage-600 hover:to-sage-700 hover:shadow-xl transition-all font-semibold px-8 py-3 rounded-lg shadow-lg text-white">

              View Google Reviews
            </a>
          </div>
        </div>
      </section>

      {/* Leave a Review */}
      <section className="py-20 bg-gradient-to-br from-sage-50 to-sage-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-sage-900 mb-4">
            Had a Great Experience?
          </h2>
          <p className="text-xl text-sage-700 mb-8 max-w-2xl mx-auto">
            We'd love to hear about your visit! Leave us a review and help other dog owners 
            discover our grooming services
          </p>
          <a
            href="https://www.google.com/search?q=pups+and+dreams+denton"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-gradient-to-r from-sage-600 to-sage-700 hover:from-sage-700 hover:to-sage-800 px-8 py-3 rounded-lg font-semibold shadow-lg transition-all text-white">
            Leave a Review
          </a>
        </div>
      </section>
    </div>);

};

export default ReviewsPage;